import { useSearchInput, useTranslations } from "@jugl-web/utils";
import { useMemo, useState } from "react";
import { SubscriptionPlanModuleId } from "@jugl-web/rest-api";
import { useConfirmationDialogState } from "@jugl-web/utils/hooks/useConfirmationDialogState";
import { SearchInput } from "@jugl-web/ui-components/cross-platform/SearchInput";
import { EmptyListContent } from "@jugl-web/ui-components";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { SettingsTabsLayout } from "../../components/SettingsTabsLayout/SettingsTabsLayout";
import {
  useWorkspaceFeatureCardData,
  WorkspaceFeature,
} from "../../hooks/useWorkspaceFeatureCardData/useWorkspaceFeatureCardData";
import { ModuleInfoProvider } from "../../providers/ModuleInfoProvider";
import { useModuleInfoProvider } from "../../providers/useModuleInfoProvider";
import { WorkspaceFeatureCard } from "./components/WorkspaceFeatureCard/WorkspaceFeatureCard";
import { ConfirmDisableModuleAlert } from "./components/ConfirmDisableModuleAlert/ConfirmDisableModuleAlert";
import { PaidFeatureDialog } from "./components/PaidFeatureDialog";
import notFoundAnimation from "./assets/not-found-animation.json";

const WorkspaceFeaturesPageContent: React.FC = () => {
  const { t } = useTranslations();
  const modulesData = useWorkspaceFeatureCardData();
  const modules = Object.keys(modulesData);
  const { showModuleInfo } = useModuleInfoProvider();
  const { searchQuery, reset, inputProps } = useSearchInput();
  const paidFeatureDialogState = useConfirmationDialogState<{
    feature: WorkspaceFeature;
  }>();
  const [alertState, setAlertState] = useState<{
    isOpen: boolean;
    module: SubscriptionPlanModuleId | null;
  }>({
    isOpen: false,
    module: null,
  });

  const handleOpenAlert = (module: SubscriptionPlanModuleId) => {
    setAlertState({
      isOpen: true,
      module,
    });
  };

  const handleCloseAlert = () => {
    setAlertState({
      isOpen: false,
      module: null,
    });
  };

  const handleConfirmDisable = () => {
    if (alertState.module) {
      modulesData[alertState.module]?.onToggle?.();
      handleCloseAlert();
    }
  };

  const filteredModules = useMemo(
    () =>
      modules
        .map((module) => {
          const key = module as SubscriptionPlanModuleId;
          const data = modulesData[key];
          return data ? { key, data } : null;
        })
        .filter(
          (
            item
          ): item is {
            key: SubscriptionPlanModuleId;
            data: WorkspaceFeature;
          } =>
            !!item &&
            item.data.hasPermission &&
            item.data.title.toLowerCase().includes(searchQuery.toLowerCase())
        ),
    [modules, modulesData, searchQuery]
  );

  return (
    <SettingsTabsLayout selectedTab="features">
      <PaidFeatureDialog
        isOpen={paidFeatureDialogState.isOpen}
        onRequestClose={paidFeatureDialogState.close}
        feature={paidFeatureDialogState.metadata?.feature}
      />
      <ConfirmDisableModuleAlert
        isOpen={alertState.isOpen}
        onConfirm={handleConfirmDisable}
        onRequestClose={handleCloseAlert}
        moduleName={
          alertState.module ? modulesData[alertState.module]?.title || "" : ""
        }
      />
      <div className="font-secondary">
        <div className="flex items-start justify-between gap-8 px-[32px] pt-[50px]">
          <div className="flex flex-col gap-2">
            <span className="text-[26px] font-semibold text-dark">
              {t({
                id: "workspace-features-page.title",
                defaultMessage: "Manage Features",
              })}
            </span>
            <span className="text-sm text-grey-900">
              {t({
                id: "workspace-features-page.description",
                defaultMessage:
                  "Enable or disable modules to customize your workspace experience.",
              })}
            </span>
          </div>
          <SearchInput
            variant="filled"
            color="grey"
            className="h-10 w-[300px]"
            {...inputProps}
            onReset={reset}
            onClear={reset}
          />
        </div>
        <div className="flex shrink-0 flex-wrap gap-8 p-10">
          {filteredModules.length ? (
            filteredModules.map((module) => {
              const { data: moduleData, key } = module;

              return (
                <WorkspaceFeatureCard
                  key={key}
                  {...moduleData}
                  onReadMoreClick={() => showModuleInfo({ moduleId: key })}
                  onOpenAlert={() => handleOpenAlert(key)}
                  onOpenPaidFeatureDialog={() =>
                    paidFeatureDialogState.open({
                      metadata: { feature: moduleData },
                    })
                  }
                  onToggle={async () => {
                    try {
                      await moduleData.onToggle?.();
                      if (!moduleData.isEnabled) {
                        showModuleInfo({ moduleId: key, showConfetti: true });
                      }
                    } catch {
                      // Do nothing
                    }
                  }}
                />
              );
            })
          ) : (
            <EmptyListContent
              type="custom"
              customImg={
                <Lottie
                  options={{ animationData: notFoundAnimation }}
                  width={250}
                  height={250}
                />
              }
              customTitle={t({
                id: "workspace-features-page.didnt-find-features-matching-search",
                defaultMessage:
                  "We didn’t find any features matching your search.",
              })}
              customSubtitle={t({
                id: "workspace-features-page.try-using-different-search",
                defaultMessage:
                  "Try using different keywords or check for typos.",
              })}
              className="w-full"
            />
          )}
        </div>
      </div>
    </SettingsTabsLayout>
  );
};

export const WorkspaceFeaturesPage = () => (
  <ModuleInfoProvider>
    <WorkspaceFeaturesPageContent />
  </ModuleInfoProvider>
);
